/**
 * 가입유형 선택 — 소셜 로그인 직후 1회 노출.
 * 학부모 / 학원 중 하나를 고르면 약관 동의 시트 → users.accountType 저장 후 홈으로.
 * 약관/개인정보는 역할별로 다르므로, 선택한 카드 기준으로 시트가 열린다.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { IoChevronForward, IoCheckmark, IoClose } from 'react-icons/io5';
import { useAuth } from '../../context/AuthContext';
import { setAccountType } from '../../services/userProfileService';
import { getLastSocialProvider } from '../../services/authService';

const AccountTypeSelectPage = () => {
  const navigate = useNavigate();
  const { currentUser, userData, refreshUser } = useAuth();
  const [saving, setSaving] = useState('');
  const [pendingRole, setPendingRole] = useState(''); // 'parent' | 'academy' | ''
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [agreeMarketing, setAgreeMarketing] = useState(false);

  const openAgree = (role) => {
    setPendingRole(role);
    setAgreeTerms(false);
    setAgreePrivacy(false);
    setAgreeMarketing(false);
  };

  const closeAgree = () => {
    if (saving) return;
    setPendingRole('');
  };

  const toggleAll = () => {
    const all = agreeTerms && agreePrivacy && agreeMarketing;
    const next = !all;
    setAgreeTerms(next);
    setAgreePrivacy(next);
    setAgreeMarketing(next);
  };

  const submit = async () => {
    if (saving || !currentUser || !pendingRole) return;
    if (!agreeTerms || !agreePrivacy) return;
    setSaving(pendingRole);
    try {
      await setAccountType({
        uid: currentUser.uid,
        accountType: pendingRole,
        name: userData?.name || currentUser.displayName || '',
        email: userData?.email || currentUser.email || '',
        provider: getLastSocialProvider() || '',
        agreements: {
          terms: agreeTerms,
          privacy: agreePrivacy,
          marketing: agreeMarketing,
        },
      });
      await refreshUser();
      if (pendingRole === 'academy') {
        // 학원은 전화번호 인증 없이 학원 정보 등록으로
        navigate('/app/biz/start', { replace: true });
      } else {
        // 학부모는 전화번호 인증 → (인증 완료 시 자녀·학원 자동매핑) → 홈
        navigate('/link-phone', { replace: true });
      }
    } catch (err) {
      console.error('가입유형 저장 실패:', err);
      alert('잠시 후 다시 시도해주세요.');
      setSaving('');
    }
  };

  const canSubmit = agreeTerms && agreePrivacy && !saving;
  const allChecked = agreeTerms && agreePrivacy && agreeMarketing;
  const accent = pendingRole === 'parent' ? '#7C3AED' : '#4A7CFE';
  const accentBg = pendingRole === 'parent' ? '#F3EEFF' : '#F4F7FF';

  return (
    <Wrap>
      <Head>
        <Title>어떻게 이용하실 건가요?</Title>
        <Sub>선택한 유형에 맞는 화면으로 시작해요</Sub>
      </Head>

      <Cards>
        <Card
          type="button"
          $type="parent"
          $on={saving === 'parent'}
          disabled={!!saving}
          onClick={() => openAgree('parent')}
        >
          <CardText>
            <CardName>학부모</CardName>
            <CardDesc>우리 아이 학원을 찾고 관리해요</CardDesc>
          </CardText>
          {saving === 'parent' ? <Spinner /> : <IoChevronForward size={20} color="rgba(255,255,255,0.8)" />}
        </Card>

        <Card
          type="button"
          $type="academy"
          $on={saving === 'academy'}
          disabled={!!saving}
          onClick={() => openAgree('academy')}
        >
          <CardText>
            <CardName>학원</CardName>
            <CardDesc>우리 학원을 등록하고 운영해요</CardDesc>
          </CardText>
          {saving === 'academy' ? <Spinner /> : <IoChevronForward size={20} color="rgba(255,255,255,0.8)" />}
        </Card>
      </Cards>

      {pendingRole && (
        <Overlay onClick={closeAgree}>
          <Sheet onClick={(e) => e.stopPropagation()}>
            <SheetHead>
              <SheetTitle>
                {pendingRole === 'academy' ? '학원' : '학부모'} 회원 약관에 동의해 주세요
              </SheetTitle>
              <CloseBtn type="button" onClick={closeAgree} aria-label="닫기">
                <IoClose size={22} />
              </CloseBtn>
            </SheetHead>
            <SheetSub>
              {pendingRole === 'academy'
                ? '학원 운영자에게 적용되는 약관이에요.'
                : '학부모 회원에게 적용되는 약관이에요.'}
            </SheetSub>

            <AllRow type="button" onClick={toggleAll} $bg={accentBg}>
              <CheckBox $on={allChecked} $accent={accent}>
                {allChecked && <IoCheckmark size={16} />}
              </CheckBox>
              <AllLabel>전체 동의</AllLabel>
            </AllRow>

            <Divider />

            <Row onClick={() => setAgreeTerms((v) => !v)}>
              <CheckBox $on={agreeTerms} $accent={accent}>
                {agreeTerms && <IoCheckmark size={16} />}
              </CheckBox>
              <RowLabel><Req $accent={accent}>[필수]</Req> 이용약관 동의</RowLabel>
              <ViewBtn
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/app/terms/${pendingRole}`);
                }}
              >
                보기
              </ViewBtn>
            </Row>

            <Row onClick={() => setAgreePrivacy((v) => !v)}>
              <CheckBox $on={agreePrivacy} $accent={accent}>
                {agreePrivacy && <IoCheckmark size={16} />}
              </CheckBox>
              <RowLabel><Req $accent={accent}>[필수]</Req> 개인정보 처리방침 동의</RowLabel>
              <ViewBtn
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/app/privacy/${pendingRole}`);
                }}
              >
                보기
              </ViewBtn>
            </Row>

            <Row onClick={() => setAgreeMarketing((v) => !v)}>
              <CheckBox $on={agreeMarketing} $accent={accent}>
                {agreeMarketing && <IoCheckmark size={16} />}
              </CheckBox>
              <RowLabel><Opt>[선택]</Opt> 마케팅 정보 수신 동의</RowLabel>
            </Row>

            <SubmitBtn type="button" disabled={!canSubmit} onClick={submit} $accent={accent}>
              {saving ? '처리 중...' : '동의하고 시작'}
            </SubmitBtn>
          </Sheet>
        </Overlay>
      )}
    </Wrap>
  );
};

export default AccountTypeSelectPage;

const Wrap = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background: #fff;
  padding: 32px 22px;
`;

const Head = styled.div`
  margin-bottom: 28px;
`;

const Title = styled.p`
  font-size: 24px;
  font-weight: 800;
  color: #1F2937;
  letter-spacing: -0.5px;
`;

const Sub = styled.p`
  margin-top: 8px;
  font-size: 15px;
  font-weight: 500;
  color: #6B7280;
`;

const Cards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Card = styled.button`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 22px 20px;
  border-radius: 16px;
  border: 2px solid ${({ $on }) => ($on ? '#fff' : 'transparent')};
  background: ${({ $type }) => ($type === 'parent' ? '#7C3AED' : '#4A7CFE')};
  text-align: left;
  transition: border-color 0.15s;
  &:disabled { opacity: ${({ $on }) => ($on ? 1 : 0.55)}; }
`;

const CardText = styled.div`
  flex: 1;
  min-width: 0;
`;

const CardName = styled.p`
  font-size: 19px;
  font-weight: 800;
  color: #fff;
`;

const CardDesc = styled.p`
  margin-top: 4px;
  font-size: 14px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
`;

const Spinner = styled.div`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border: 3px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  @keyframes spin { to { transform: rotate(360deg); } }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 300;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

const Sheet = styled.div`
  width: 100%;
  max-width: 480px;
  background: #fff;
  border-radius: 20px 20px 0 0;
  padding: 22px 20px 28px;
  max-height: 90vh;
  overflow-y: auto;
`;

const SheetHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const SheetTitle = styled.p`
  font-size: 18px;
  font-weight: 800;
  color: #111827;
`;

const CloseBtn = styled.button`
  flex-shrink: 0;
  color: #6B7280;
  background: transparent;
`;

const SheetSub = styled.p`
  margin-top: 6px;
  margin-bottom: 18px;
  font-size: 13.5px;
  color: #6B7280;
`;

const AllRow = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 12px;
  border-radius: 12px;
  background: ${({ $bg }) => $bg || '#F4F7FF'};
  text-align: left;
`;

const AllLabel = styled.span`
  font-size: 15px;
  font-weight: 800;
  color: #111827;
`;

const Divider = styled.div`
  height: 1px;
  background: #EEF0F3;
  margin: 14px 0 6px;
`;

/* button 이 아니라 div — 안에 "보기"(ViewBtn) 버튼이 들어가서
   button 중첩(hydration error) 방지. 클릭 토글은 onClick 으로 동작. */
const Row = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 13px 12px;
  background: transparent;
  text-align: left;
  cursor: pointer;
`;

const RowLabel = styled.span`
  flex: 1;
  font-size: 14.5px;
  font-weight: 600;
  color: #1F2937;
`;

const Req = styled.span`
  color: ${({ $accent }) => $accent || '#4A7CFE'};
  font-weight: 800;
  margin-right: 4px;
`;

const Opt = styled.span`
  color: #9CA3AF;
  font-weight: 700;
  margin-right: 4px;
`;

const ViewBtn = styled.button`
  flex-shrink: 0;
  padding: 6px 10px;
  border-radius: 8px;
  background: #F4F5F7;
  font-size: 12.5px;
  font-weight: 700;
  color: #4B5563;
`;

const CheckBox = styled.span`
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $on, $accent }) => ($on ? ($accent || '#4A7CFE') : '#E5E7EB')};
  color: #fff;
`;

const SubmitBtn = styled.button`
  width: 100%;
  margin-top: 22px;
  padding: 16px;
  border-radius: 14px;
  background: ${({ $accent }) => $accent || '#4A7CFE'};
  color: #fff;
  font-size: 16px;
  font-weight: 800;
  &:disabled {
    background: #D7D9E0;
    color: #fff;
  }
`;
